import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  Trash2,
  X,
  AlertCircle,
} from 'lucide-react';
import {
  useAccounts,
  useCreateAccount,
  useTestAccountConnection,
  useDeleteAccount,
  useUpdateAccount,
} from '../hooks/useAccounts';
import ManualSpendEditor from '../components/accounts/ManualSpendEditor';

type Platform = 'facebook' | 'tiktok' | 'google' | 'youtube';

interface AddAccountForm {
  platform: Platform;
  name: string;
  platformAccountId: string;
  accessToken: string;
  refreshToken: string;
}

const initialFormState: AddAccountForm = {
  platform: 'facebook',
  name: '',
  platformAccountId: '',
  accessToken: '',
  refreshToken: '',
};

export default function Accounts() {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [formData, setFormData] = useState<AddAccountForm>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string } | null>>({});
  const [testingAll, setTestingAll] = useState(false);

  const { data: accounts, isLoading: accountsLoading } = useAccounts();
  const createAccount = useCreateAccount();
  const testConnection = useTestAccountConnection();
  const deleteAccount = useDeleteAccount();
  const updateAccount = useUpdateAccount();

  // Inline-edit draft for the per-account `adNameFilter` field.
  const [filterDrafts, setFilterDrafts] = useState<Record<string, string>>({});

  // Sequentially test every account's connection and record each result.
  const handleTestAll = async () => {
    if (!accounts?.length) return;
    setTestingAll(true);
    setTestResults({});
    for (const account of accounts) {
      try {
        const result = await testConnection.mutateAsync(account._id);
        setTestResults((prev) => ({
          ...prev,
          [account._id]: {
            success: result.success,
            message: result.success
              ? 'Connection successful!'
              : (result.error || 'Connection failed'),
          },
        }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Connection test failed';
        setTestResults((prev) => ({
          ...prev,
          [account._id]: { success: false, message },
        }));
      }
    }
    setTestingAll(false);
  };

  const handleInputChange = (field: keyof AddAccountForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.name.trim()) {
      setFormError('Account name is required');
      return;
    }
    if (!formData.platformAccountId.trim()) {
      setFormError('Ad Account ID is required');
      return;
    }
    if (!formData.accessToken.trim()) {
      setFormError('Access Token is required');
      return;
    }

    // Ensure platformAccountId has act_ prefix for Facebook
    let accountId = formData.platformAccountId.trim();
    if (formData.platform === 'facebook' && !accountId.startsWith('act_')) {
      accountId = `act_${accountId}`;
    }

    try {
      await createAccount.mutateAsync({
        platform: formData.platform,
        name: formData.name.trim(),
        platformAccountId: accountId,
        accessToken: formData.accessToken.trim(),
        refreshToken: formData.refreshToken.trim() || undefined,
      });
      setShowAddModal(false);
      setFormData(initialFormState);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create account';
      setFormError(message);
    }
  };

  const handleTestConnection = async (accountId: string) => {
    setTestResults((prev) => ({ ...prev, [accountId]: null }));
    try {
      const result = await testConnection.mutateAsync(accountId);
      setTestResults((prev) => ({
        ...prev,
        [accountId]: {
          success: result.success,
          message: result.success ? 'Connection successful!' : (result.error || 'Connection failed'),
        },
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Connection test failed';
      setTestResults((prev) => ({
        ...prev,
        [accountId]: { success: false, message },
      }));
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    try {
      await deleteAccount.mutateAsync(accountId);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setFormData(initialFormState);
    setFormError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('accounts.title')}</h1>

        <div className="flex items-center gap-2">
          <button
            onClick={handleTestAll}
            disabled={testingAll || !accounts?.length}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {testingAll ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {t('accounts.testAllConnections')}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            {t('accounts.addAccount')}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {accountsLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Accounts Grid */}
      {!accountsLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {accounts?.map((account) => (
            <div
              key={account._id}
              className="rounded-lg border bg-white p-6 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                    <span className="text-lg font-bold text-blue-600">
                      {account.platform === 'facebook' ? 'f' : account.platform[0]}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{account.name}</h3>
                    <p className="text-sm text-gray-500">{account.platformAccountId}</p>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowDeleteConfirm(account._id)}
                    className="rounded-md p-1 hover:bg-gray-100"
                    title="Delete account"
                  >
                    <Trash2 className="h-5 w-5 text-gray-400 hover:text-red-500" />
                  </button>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {account.isActive ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  <span className="text-sm text-gray-600">
                    {account.isActive ? t('accounts.active') : t('accounts.inactive')}
                  </span>
                </div>

                {account.lastSyncAt && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <RefreshCw className="h-4 w-4" />
                    <span>
                      {new Date(account.lastSyncAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Manual accounts (Hebe, Rabota): spend entries editor instead
                  of the ad-name filter — they have no ads to filter. */}
              {account.platform === 'manual' && (
                <ManualSpendEditor accountId={account._id} />
              )}

              {/* Name filter — pin this account to ads/forms containing a substring */}
              {account.platform !== 'manual' && (
              <div className="mt-4 border-t border-gray-100 pt-3">
                <label className="block text-xs font-medium text-gray-600">
                  {t('accounts.nameFilterLabel')}
                </label>
                <div className="mt-1 flex gap-2">
                  <input
                    type="text"
                    placeholder={t('accounts.nameFilterPlaceholder')}
                    value={filterDrafts[account._id] ?? account.adNameFilter ?? ''}
                    onChange={(e) =>
                      setFilterDrafts((d) => ({ ...d, [account._id]: e.target.value }))
                    }
                    className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                  <button
                    onClick={() => {
                      const v = (filterDrafts[account._id] ?? account.adNameFilter ?? '').trim();
                      updateAccount.mutate(
                        { id: account._id, data: { adNameFilter: v === '' ? null : v } },
                        {
                          onSuccess: () =>
                            setFilterDrafts((d) => {
                              const c = { ...d };
                              delete c[account._id];
                              return c;
                            }),
                        }
                      );
                    }}
                    disabled={
                      updateAccount.isPending ||
                      (filterDrafts[account._id] ?? account.adNameFilter ?? '') ===
                        (account.adNameFilter ?? '')
                    }
                    className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-40"
                  >
                    {t('common.save')}
                  </button>
                </div>
                {account.adNameFilter && (
                  <p className="mt-1 text-xs text-gray-500">
                    {t('accounts.nameFilterActive', { filter: account.adNameFilter })}
                  </p>
                )}
              </div>
              )}

              {/* Test Result Feedback */}
              {testResults[account._id] && (
                <div
                  className={`mt-3 rounded-md p-2 text-sm ${
                    testResults[account._id]?.success
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {testResults[account._id]?.message}
                </div>
              )}

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleTestConnection(account._id)}
                  disabled={testConnection.isPending}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {testConnection.isPending ? (
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  ) : (
                    t('accounts.testConnection')
                  )}
                </button>
              </div>
            </div>
          ))}

          {/* Empty State - Add Account Card */}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white p-6 text-gray-500 hover:border-primary hover:text-primary"
          >
            <Plus className="h-8 w-8" />
            <span className="mt-2 font-medium">{t('accounts.addAccount')}</span>
          </button>
        </div>
      )}

      {/* Add Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{t('accounts.addAccount')}</h2>
              <button
                onClick={closeModal}
                className="rounded-md p-1 hover:bg-gray-100"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleAddAccount} className="space-y-4">
              {/* Platform Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Platform
                </label>
                <select
                  value={formData.platform}
                  onChange={(e) => handleInputChange('platform', e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="facebook">Facebook</option>
                  <option value="tiktok" disabled>TikTok (Coming Soon)</option>
                  <option value="google" disabled>Google (Coming Soon)</option>
                  <option value="youtube" disabled>YouTube (Coming Soon)</option>
                </select>
              </div>

              {/* Account Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., My Business Ads"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Ad Account ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ad Account ID *
                </label>
                <input
                  type="text"
                  value={formData.platformAccountId}
                  onChange={(e) => handleInputChange('platformAccountId', e.target.value)}
                  placeholder="act_XXXXXXXXXX or just the number"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Find in Ads Manager URL or Business Settings → Ad Accounts
                </p>
              </div>

              {/* Access Token */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Token *
                </label>
                <textarea
                  value={formData.accessToken}
                  onChange={(e) => handleInputChange('accessToken', e.target.value)}
                  placeholder="EAAxxxxxxx..."
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Get from Graph API Explorer → exchange for long-lived token
                </p>
              </div>

              {/* Refresh Token (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zoho Refresh Token (Optional)
                </label>
                <input
                  type="text"
                  value={formData.refreshToken}
                  onChange={(e) => handleInputChange('refreshToken', e.target.value)}
                  placeholder="For Zoho Bigin deal matching"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Only needed if you want to match Facebook leads with Zoho deals
                </p>
              </div>

              {/* Error Message */}
              {formError && (
                <div className="flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {formError}
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createAccount.isPending}
                  className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {createAccount.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('accounts.addAccount')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Delete Account?</h3>
            <p className="mt-2 text-sm text-gray-500">
              Are you sure you want to delete this account? This action cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteAccount(showDeleteConfirm)}
                disabled={deleteAccount.isPending}
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteAccount.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
